import { z } from 'zod';
import { OBJECT_NAMES } from './objects';
import { filterSchema } from './validation';

const uuid = z.string().uuid();
const password = z
  .string()
  .min(8, 'Use at least 8 characters')
  .max(256);

// --- Lead conversion -------------------------------------------------------
export const leadConvertSchema = z
  .object({
    /** Attach to an existing account, or create one named after the lead's company. */
    accountId: uuid.nullable().optional(),
    /** Attach to an existing contact (only empty fields are filled), or create one. */
    contactId: uuid.nullable().optional(),
    createOpportunity: z.boolean().default(true),
    opportunityName: z.string().trim().min(1).max(255).optional(),
    ownerId: uuid.optional(),
    /** Convert to a person account (an individual) instead of a business account and contact. */
    personAccount: z.boolean().default(false),
  })
  .strict()
  .refine((v) => !v.personAccount || (!v.accountId && !v.contactId), { path: ['personAccount'], message: 'A person account is always new' });
export type LeadConvertInput = z.infer<typeof leadConvertSchema>;

export interface LeadConvertResult {
  accountId: string;
  contactId: string;
  opportunityId: string | null;
}

// --- Opportunity contact roles --------------------------------------------
export const CONTACT_ROLES = [
  { value: 'decision_maker', label: 'Decision maker' },
  { value: 'economic_buyer', label: 'Economic buyer' },
  { value: 'technical_buyer', label: 'Technical buyer' },
  { value: 'evaluator', label: 'Evaluator' },
  { value: 'influencer', label: 'Influencer' },
  { value: 'executive_sponsor', label: 'Executive sponsor' },
  { value: 'business_user', label: 'Business user' },
  { value: 'other', label: 'Other' },
] as const;

export const contactRoleSchema = z
  .object({
    contactId: uuid,
    role: z.enum(['decision_maker', 'economic_buyer', 'technical_buyer', 'evaluator', 'influencer', 'executive_sponsor', 'business_user', 'other']).nullable().optional(),
    isPrimary: z.boolean().default(false),
  })
  .strict();

export interface ContactRole {
  id: string;
  opportunityId: string;
  contactId: string;
  contactName: string;
  opportunityName: string;
  role: string | null;
  isPrimary: boolean;
}

// --- Saved list views ------------------------------------------------------
export const listViewSchema = z
  .object({
    objectName: z.enum(OBJECT_NAMES),
    name: z.string().trim().min(1).max(80),
    visibility: z.enum(['private', 'org']).default('private'),
    definition: z
      .object({
        q: z.string().max(200).optional(),
        filters: z.array(filterSchema).max(20).default([]),
        sort: z.string().max(64).optional(),
        dir: z.enum(['asc', 'desc']).optional(),
        mine: z.boolean().optional(),
        /** The columns shown, in order (empty or missing = the default columns). */
        columns: z.array(z.string().min(1).max(64)).max(30).optional(),
      })
      .strict(),
  })
  .strict();
export type ListViewInput = z.infer<typeof listViewSchema>;

export interface ListView extends ListViewInput {
  id: string;
  ownerId: string;
  isMine: boolean;
}

// --- Setup: users, stages, sharing ----------------------------------------
export const inviteSchema = z
  .object({
    email: z.string().trim().toLowerCase().email().max(255),
    permissionSetIds: z.array(uuid).min(1, 'Choose at least one permission set').max(20),
  })
  .strict();

export const acceptInviteSchema = z
  .object({
    token: z.string().min(20).max(100),
    /** Required when the email has no account yet. */
    name: z.string().trim().min(1).max(120).optional(),
    password,
  })
  .strict();

export const passwordResetRequestSchema = z.object({ email: z.string().trim().toLowerCase().email().max(255) }).strict();
export const passwordResetConfirmSchema = z.object({ token: z.string().min(20).max(100), password }).strict();

/** A user's own profile (My settings). */
export const profileUpdateSchema = z.object({ name: z.string().trim().min(1, 'Enter your name').max(120) }).strict();

/** Person accounts can be turned off again only while none exist. */
export const personAccountSettingsSchema = z.object({ enabled: z.boolean() }).strict();

export const memberUpdateSchema = z
  .object({
    /** Display name (admins can correct it, e.g. when someone typed their email as their name). */
    name: z.string().trim().min(1, 'Enter a name').max(120).optional(),
    permissionSetIds: z.array(uuid).min(1).max(20).optional(),
    isActive: z.boolean().optional(),
    /** When deactivating: reassign everything the user owns in this org to this user. */
    transferToUserId: uuid.optional(),
  })
  .strict();

export const stageSchema = z
  .object({
    name: z.string().trim().min(1).max(60),
    probability: z.number().min(0).max(100),
    forecastCategory: z.enum(['pipeline', 'best_case', 'commit', 'closed', 'omitted']),
    isClosed: z.boolean(),
    isWon: z.boolean(),
    isActive: z.boolean().default(true),
    guidance: z.string().max(2000).default(''),
    /** From this stage on, the quotation has been sent (KPIs, Deal Intelligence, quotation submitted date). */
    marksQuoted: z.boolean().default(false),
    /** On create: the pipeline the stage belongs to (the default pipeline when omitted). Ignored on update. */
    pipelineId: uuid.optional(),
  })
  .strict()
  .refine((s) => !s.isWon || s.isClosed, { message: 'A won stage must also be closed', path: ['isWon'] });

export const stageOrderSchema = z.object({ ids: z.array(uuid).min(1).max(50) }).strict();

export const sharingSchema = z
  .object(Object.fromEntries(OBJECT_NAMES.map((o) => [o, (o === 'contact' ? z.enum(['private', 'public_read', 'public_read_write', 'controlled_by_parent']) : z.enum(['private', 'public_read', 'public_read_write'])).optional()])))
  .strict();

export interface Member {
  userId: string;
  name: string;
  email: string;
  isActive: boolean;
  lastLoginAt: string | null;
  /** When the person last used the CRM (any of their sessions, "Log in as" excluded). */
  lastActiveAt: string | null;
  roleId: string | null;
  permissionSetIds: string[];
}

export interface PermissionSetSummary {
  id: string;
  key: string;
  name: string;
  description: string;
}

export interface PendingInvite {
  id: string;
  email: string;
  createdAt: string;
  expiresAt: string;
}

export interface RecycleBinItem {
  objectName: string;
  id: string;
  name: string;
  deletedAt: string;
  deletedByName: string | null;
}

export interface RecentItem {
  objectName: string;
  recordId: string;
  name: string;
  viewedAt: string;
}
